/**
 * Watch Winders — global theme behaviours.
 * Currently: scroll-reveal for [data-reveal] / [data-reveal-stagger] elements.
 * (Cart, wishlist, search, carousels, etc. are added in later phases.)
 */
(function () {
  'use strict';

  function initReveal() {
    var els = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (!els.length) return;

    // No IntersectionObserver (or reduced motion): just show everything.
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduce) {
      els.forEach(function (el) { el.classList.add('in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* Update the header cart-count badge(s) from the live cart. */
  function updateCartCount() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
        });
      })
      .catch(function () {});
  }

  /* Quick-view modal: fetch a compact product view and wire variants + AJAX add. */
  function initQuickView() {
    var modal = document.querySelector('[data-quick-view]');
    if (!modal) return;
    var body = modal.querySelector('[data-quick-view-body]');
    var lastFocus = null;
    var spinner = '<div class="qv-spinner" data-qv-loading></div>';

    function setOpen(open) {
      modal.classList.toggle('open', open);
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (!open) { body.innerHTML = spinner; }
    }

    function openFor(url) {
      lastFocus = document.activeElement;
      body.innerHTML = spinner;
      setOpen(true);
      var sep = url.indexOf('?') > -1 ? '&' : '?';
      fetch(url + sep + 'view=quick-view', { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = doc.querySelector('[data-quick-view-content]');
          if (!content) { body.innerHTML = '<p style="padding:48px;text-align:center;color:var(--ink-soft)">Unable to load this item.</p>'; return; }
          body.innerHTML = '';
          body.appendChild(content);
          initContent(content);
        })
        .catch(function () { body.innerHTML = '<p style="padding:48px;text-align:center;color:var(--ink-soft)">Unable to load this item.</p>'; });
    }

    function initContent(root) {
      var mainImg = root.querySelector('[data-qv-image]');

      root.querySelectorAll('[data-qv-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var full = thumb.getAttribute('data-full');
          if (full && mainImg) { mainImg.src = full; }
          root.querySelectorAll('[data-qv-thumb]').forEach(function (t) { t.classList.remove('on'); });
          thumb.classList.add('on');
        });
      });

      var variants = [];
      var dataEl = root.querySelector('[data-qv-variants]');
      if (dataEl) { try { variants = JSON.parse(dataEl.textContent); } catch (e) { variants = []; } }
      var selects = root.querySelectorAll('[data-qv-option]');
      var idInput = root.querySelector('[data-qv-id]');
      var priceEl = root.querySelector('[data-qv-price]');
      var compareEl = root.querySelector('[data-qv-compare]');
      var addBtn = root.querySelector('[data-qv-add]');
      var addText = root.querySelector('[data-qv-add-text]');
      var labelAdd = addBtn ? (addBtn.getAttribute('data-label-add') || 'Add to Cart') : 'Add to Cart';
      var labelSold = addBtn ? (addBtn.getAttribute('data-label-sold') || 'Sold out') : 'Sold out';
      var labelAdded = addBtn ? (addBtn.getAttribute('data-label-added') || 'Added') : 'Added';

      function currentVariant() {
        var chosen = [];
        selects.forEach(function (s) { chosen.push(s.value); });
        return variants.filter(function (v) {
          return v.options.length === chosen.length && v.options.every(function (o, i) { return o === chosen[i]; });
        })[0];
      }

      function onChange() {
        var v = currentVariant();
        if (!v) { return; }
        if (idInput) { idInput.value = v.id; }
        if (priceEl) { priceEl.textContent = v.price; }
        if (compareEl) {
          if (v.compare && v.compare !== v.price) { compareEl.textContent = v.compare; compareEl.style.display = ''; }
          else { compareEl.style.display = 'none'; }
        }
        if (v.image && mainImg) { mainImg.src = v.image; }
        if (addBtn) { addBtn.disabled = !v.available; }
        if (addText) { addText.textContent = v.available ? labelAdd : labelSold; }
      }
      selects.forEach(function (s) { s.addEventListener('change', onChange); });
      // Add-to-cart is handled globally by initCart's form interceptor,
      // which closes this quick view and opens the cart drawer.
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-quick]');
      if (!trigger) return;
      e.preventDefault();
      var url = trigger.getAttribute('data-quick');
      if (url) { openFor(url); }
    });
    modal.querySelectorAll('[data-quick-view-close]').forEach(function (el) {
      el.addEventListener('click', function () { setOpen(false); if (lastFocus && lastFocus.focus) { lastFocus.focus(); } });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('open')) { setOpen(false); }
    });
  }

  /* Favourites (wishlist): localStorage-backed, with a slide-out drawer. */
  function initWishlist() {
    var KEY = 'ww_wishlist';
    var drawer = document.querySelector('[data-wishlist]');
    var listEl = drawer && drawer.querySelector('[data-wishlist-list]');
    var emptyEl = drawer && drawer.querySelector('[data-wishlist-empty]');
    var footEl = drawer && drawer.querySelector('[data-wishlist-foot]');
    var countLabel = drawer && drawer.querySelector('[data-wishlist-count-label]');
    var ICON_VIEW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M5 12h14M13 6l6 6-6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    var ICON_DEL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M6 6 18 18M18 6 6 18" stroke-linecap="round"/></svg>';

    function read() { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } }
    function write(list) { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {} }
    function has(list, id) { return list.some(function (i) { return String(i.id) === String(id); }); }
    function esc(s) { return (s || '').replace(/"/g, '&quot;'); }

    function updateCounts(list) {
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = list.length;
        if (list.length > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
      });
      if (countLabel) countLabel.textContent = list.length;
    }
    function renderDrawer(list) {
      if (!listEl) return;
      if (!list.length) {
        listEl.innerHTML = '';
        if (emptyEl) emptyEl.classList.add('show');
        if (footEl) footEl.setAttribute('hidden', '');
        return;
      }
      if (emptyEl) emptyEl.classList.remove('show');
      if (footEl) footEl.removeAttribute('hidden');
      listEl.innerHTML = list.map(function (i) {
        return '<div class="wishd-row" data-wish-row="' + esc(i.id) + '">' +
          '<a class="wr-media" href="' + esc(i.url) + '">' + (i.image ? '<img src="' + esc(i.image) + '" alt="">' : '') + '</a>' +
          '<div class="wr-body">' +
            (i.brand ? '<small>' + esc(i.brand) + '</small>' : '') +
            '<b><a href="' + esc(i.url) + '">' + esc(i.title) + '</a></b>' +
            '<span class="wr-price">' + esc(i.price) + '</span>' +
          '</div>' +
          '<div class="wishd-acts">' +
            '<a class="wr-btn" href="' + esc(i.url) + '" aria-label="View">' + ICON_VIEW + '</a>' +
            '<button type="button" class="wr-btn del" data-wish-remove="' + esc(i.id) + '" aria-label="Remove">' + ICON_DEL + '</button>' +
          '</div>' +
        '</div>';
      }).join('');
    }
    function syncHearts(list) {
      document.querySelectorAll('[data-wish]').forEach(function (btn) {
        var on = has(list, btn.getAttribute('data-wish'));
        btn.classList.toggle('on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    function refresh() { var list = read(); updateCounts(list); renderDrawer(list); syncHearts(list); }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wish]');
      if (btn) {
        e.preventDefault();
        var id = btn.getAttribute('data-wish');
        var list = read();
        if (has(list, id)) {
          list = list.filter(function (i) { return String(i.id) !== String(id); });
        } else {
          list.push({
            id: id,
            variant: btn.getAttribute('data-wish-variant') || '',
            title: btn.getAttribute('data-wish-title') || '',
            url: btn.getAttribute('data-wish-url') || '#',
            image: btn.getAttribute('data-wish-image') || '',
            price: btn.getAttribute('data-wish-price') || '',
            brand: btn.getAttribute('data-wish-brand') || ''
          });
        }
        write(list);
        refresh();
        return;
      }
      var rem = e.target.closest('[data-wish-remove]');
      if (rem) {
        var rid = rem.getAttribute('data-wish-remove');
        write(read().filter(function (i) { return String(i.id) !== String(rid); }));
        refresh();
      }
    });

    if (drawer) {
      var setOpen = function (open) {
        drawer.classList.toggle('open', open);
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        document.documentElement.style.overflow = open ? 'hidden' : '';
      };
      document.querySelectorAll('[data-wishlist-open]').forEach(function (b) { b.addEventListener('click', function () { setOpen(true); }); });
      drawer.querySelectorAll('[data-wishlist-close]').forEach(function (el) { el.addEventListener('click', function () { setOpen(false); }); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('open')) setOpen(false); });

      /* "Add items to cart" — add every favourite to the cart, then go to cart. */
      var checkoutBtn = drawer.querySelector('[data-wishlist-checkout]');
      var checkoutText = drawer.querySelector('[data-wishlist-checkout-text]');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', function () {
          var items = read()
            .filter(function (i) { return i.variant; })
            .map(function (i) { return { id: i.variant, quantity: 1 }; });
          if (!items.length) return;
          checkoutBtn.disabled = true;
          if (checkoutText) checkoutText.textContent = 'Adding…';
          var done = function () { checkoutBtn.disabled = false; if (checkoutText) checkoutText.textContent = 'Add items to cart'; };
          if (window.wwCartAdd) {
            window.wwCartAdd(items, true)
              .then(function () { setOpen(false); done(); })
              .catch(done);
          } else {
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: items })
            })
              .then(function (r) { return r.json(); })
              .then(function () { window.location.href = '/cart'; })
              .catch(done);
          }
        });
      }
    }

    refresh();
    window.addEventListener('storage', function (e) { if (e.key === KEY) refresh(); });
    // Let dynamically-injected cards (e.g. Recently viewed) reflect wishlist state.
    window.wwSyncWishlist = function () { syncHearts(read()); };
  }

  /* Cart drawer: AJAX add / update / remove with re-rendered section HTML. */
  function initCart() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    var contents = drawer.querySelector('[data-cart-contents]');
    var countLabel = drawer.querySelector('[data-cart-count-label]');

    function setOpen(open) {
      drawer.classList.toggle('open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.style.overflow = open ? 'hidden' : '';
    }
    function setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = n;
        if (n > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
      });
      if (countLabel) countLabel.textContent = n;
    }
    function applySections(sections) {
      if (!sections || !sections['cart-drawer'] || !contents) return;
      var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var nc = doc.querySelector('[data-cart-contents]');
      if (nc) {
        contents.innerHTML = nc.innerHTML;
        var c = contents.querySelector('[data-cart-total-count]');
        if (c) setCount(parseInt(c.textContent, 10) || 0);
      }
    }
    function loading(on) { drawer.classList.toggle('is-loading', on); }

    function change(key, quantity) {
      loading(true);
      fetch('/cart/change.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity, sections: 'cart-drawer', sections_url: window.location.pathname })
      }).then(function (r) { return r.json(); }).then(function (cart) { applySections(cart.sections); loading(false); }).catch(function () { loading(false); });
    }
    function add(items, openDrawer) {
      loading(true);
      return fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: 'cart-drawer', sections_url: window.location.pathname })
      })
        .then(function (r) { if (!r.ok) { return r.json().then(function (er) { throw er; }); } return r.json(); })
        .then(function (res) { applySections(res.sections); loading(false); if (openDrawer !== false) setOpen(true); return res; })
        .catch(function (e) { loading(false); throw e; });
    }
    window.wwCartAdd = add;
    window.wwCartOpen = function () { setOpen(true); };

    document.querySelectorAll('[data-cart-toggle]').forEach(function (b) {
      b.addEventListener('click', function (e) { e.preventDefault(); setOpen(true); });
    });
    drawer.addEventListener('click', function (e) { if (e.target.closest('[data-cart-close]')) setOpen(false); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && drawer.classList.contains('open')) setOpen(false); });

    drawer.addEventListener('click', function (e) {
      var rem = e.target.closest('[data-cart-remove]');
      if (rem) { change(rem.getAttribute('data-key'), 0); return; }
      var minus = e.target.closest('[data-cart-minus]');
      var plus = e.target.closest('[data-cart-plus]');
      if (minus || plus) {
        var btn = minus || plus;
        var key = btn.getAttribute('data-key');
        var inp = drawer.querySelector('[data-cart-qty][data-key="' + key + '"]');
        var q = inp ? parseInt(inp.value, 10) || 1 : 1;
        q = minus ? Math.max(0, q - 1) : q + 1;
        change(key, q);
      }
    });
    drawer.addEventListener('change', function (e) {
      var inp = e.target.closest('[data-cart-qty]');
      if (inp) { change(inp.getAttribute('data-key'), Math.max(0, parseInt(inp.value, 10) || 0)); }
    });

    /* Product-card "Add to Cart" (single-variant) → AJAX + open drawer. */
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-card-add]');
      if (!b || b.disabled) return;
      e.preventDefault();
      var id = b.getAttribute('data-variant');
      if (!id) return;
      b.disabled = true;
      add([{ id: id, quantity: 1 }], true).then(function () { b.disabled = false; }).catch(function () { b.disabled = false; });
    });

    /* Intercept all product add-to-cart forms → AJAX + open drawer (covers PDP, deal, quick-view). */
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action$="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      var fd = new FormData(form);
      var id = fd.get('id');
      if (!id) { form.submit(); return; }
      var qty = parseInt(fd.get('quantity') || '1', 10) || 1;
      var btn = form.querySelector('button[name="add"], [type="submit"]');
      if (btn) btn.disabled = true;
      var qv = form.closest('[data-quick-view]');
      add([{ id: id, quantity: qty }], true)
        .then(function () {
          if (qv) { qv.classList.remove('open'); qv.setAttribute('aria-hidden', 'true'); }
          if (btn) btn.disabled = false;
        })
        .catch(function () { if (btn) btn.disabled = false; });
    });
  }

  function init() {
    initReveal();
    initQuickView();
    initWishlist();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
